import { AVATAR_EDGE } from "@rally/agreed";
import { useEffect, useState } from "react";
import { say } from "../localization/say.ts";
import { act, upload } from "../net/rally.ts";

const held = new Map<string, string>();

const asking = new Map<string, Promise<string | null>>();

async function bring(id: string): Promise<string | null> {
  const already = held.get(id);
  if (already) return already;
  const running = asking.get(id);
  if (running) return running;
  const going = (async () => {
    try {
      const opened = await act<{ url: string }>("avatar.open", { avatar: id });
      const got = await fetch(opened.url);
      if (!got.ok) return null;
      const url = URL.createObjectURL(await got.blob());
      held.set(id, url);
      return url;
    } catch {
      return null;
    } finally {
      asking.delete(id);
    }
  })();
  asking.set(id, going);
  return going;
}

export function useFace(id: string | null | undefined): string | undefined {
  const [url, setUrl] = useState<string | undefined>(() => (id ? held.get(id) : undefined));
  useEffect(() => {
    if (!id) {
      setUrl(undefined);
      return;
    }
    let alive = true;
    void bring(id).then((got) => {
      if (alive) setUrl(got ?? undefined);
    });
    return () => {
      alive = false;
    };
  }, [id]);
  return url;
}

function oneFace(subject?: string): { subject?: string } {
  return subject ? { subject } : {};
}

export async function wearAFace(file: File, subject?: string): Promise<string> {
  const square = await squareOf(file);

  if (!square) throw new Error(say("you.notAPicture"));
  const taken = await upload<{ key: string }>("avatar.upload", oneFace(subject), square);
  const said = await act<{ avatar: string }>("avatar.set", { key: taken.key, ...oneFace(subject) });
  return said.avatar;
}

export async function dropAFace(subject?: string): Promise<void> {
  await act("avatar.remove", oneFace(subject));
}

export async function squareOf(file: File): Promise<Blob | null> {
  const source = await createImageBitmap(file).catch(() => null);
  if (!source) return null;
  try {
    const edge = Math.min(source.width, source.height);
    const canvas = document.createElement("canvas");
    canvas.width = AVATAR_EDGE;
    canvas.height = AVATAR_EDGE;
    const paper = canvas.getContext("2d");
    if (!paper) return null;

    const left = (source.width - edge) / 2;
    const top = (source.height - edge) / 2;
    paper.drawImage(source, left, top, edge, edge, 0, 0, AVATAR_EDGE, AVATAR_EDGE);
    return await new Promise((done) => canvas.toBlob(done, "image/webp", 0.82));
  } finally {
    source.close();
  }
}
